import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Button, Stack } from '@curves/ui-base';

import { PageLayout } from '@Local/Components/Layout';
import { CAMPAIGN_DISCOUNT_METHOD } from '@Constants';
import useOptions from '@Util/hook/useOptions';
import useMessageDialog from '@Util/hook/useMessageDialog';
import useValidationErrors from '@Util/hook/useValidationErrors';
import { ensureArray, isEmpty } from '@Util/utils';
import useCampaignApi from '@Apis/useCampaignApi';
import useProductApi from '@Apis/useProductApi';
import { CAMPAIGN_OVERVIEW_ROUTER } from '@Router/routerPath';
import CampaignBasicForm from './CampaignBasicForm';
import CampaignProductLimit from './CampaignProductLimit';
import { LIMIT_ITEMS_REQUIRED_MESSAGE, buildCampaignPayload, buildDefaultIncludeItems, getCampaignTypeValue, getItemKey, isDefaultIncludeItems } from './CampaignEdit.config';

// 新增時的商品限制預設值
const EMPTY_LIMIT = {
    isLimited: false,
    campaignType: '',
    includeItems: [],
    excludeItems: [],
};

// 另一份名單的欄位名稱與顯示名稱
const OTHER_LIST = {
    includeItems: { key: 'excludeItems', label: '黑名單' },
    excludeItems: { key: 'includeItems', label: '白名單' },
};

// 將後端讀回的名單項目轉成畫面使用的格式
const toListItems = items => ensureArray(items).map(({ referenceKind, referenceID, displayName, isDeleted }) => ({ referenceKind, referenceID, displayName, isDeleted: !!isDeleted }));

/**
 * HQ 抵用券新增／編輯頁
 * - 路由有 campaignID 為編輯，沒有為新增
 * - 資料載入完成才顯示表單，載入中由 useAxios 的遮罩處理，失敗由 useAxios 提示且不顯示表單
 * - 已被訂單使用的券僅能修改使用期間與商品限制
 */
const CampaignEditPage = () => {
    const { campaignID } = useParams();
    const isEdit = !!campaignID;
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();
    const setMessageDialog = useMessageDialog();
    const { getCampaignOptionsApi, getCampaignApi, postCampaignApi, putCampaignApi } = useCampaignApi();
    const { getSaleCategoriesApi } = useProductApi();

    // 取得表單選項（不含「全部」）
    const getOptionsApi = useCallback(() => getCampaignOptionsApi({ skipAll: true }), [getCampaignOptionsApi]);
    // 後端提供的表單選項
    const { options } = useOptions({ getOptionsApi, isFetchOnMount: true });
    // 分類樹與讀回的抵用券（新增時 campaign 為 null），載入完成前為 null
    const [pageData, setPageData] = useState(null);
    // 商品限制區塊的內容，會即時改變畫面所以用 state；campaignType 只記錄使用者選過的類型，不送出
    const [limit, setLimit] = useState(EMPTY_LIMIT);
    // 基本資料與折扣設定的 ref，送出時檢核與取值
    const basicFormRef = useRef(null);
    // 後端欄位驗證錯誤（0002），標示到對應欄位
    const { validationErrors, updateValidationErrors, clearValidationErrors } = useValidationErrors();

    // 載入分類樹，編輯時一併讀回抵用券；讀不到券導回總覽
    const fetchPageData = useCallback(async () => {
        const [categoryTrees, campaign] = await Promise.all([getSaleCategoriesApi(), campaignID ? getCampaignApi(campaignID) : Promise.resolve(null)]);
        if (campaignID && !campaign) {
            history.replace(CAMPAIGN_OVERVIEW_ROUTER);
            return;
        }
        if (!Array.isArray(categoryTrees)) return;
        if (campaign) {
            setLimit({
                ...EMPTY_LIMIT,
                isLimited: !!(campaign.discountMethod & CAMPAIGN_DISCOUNT_METHOD.PRODUCT_LIMIT),
                includeItems: toListItems(campaign.includeItems),
                excludeItems: toListItems(campaign.excludeItems),
            });
        }
        setPageData({ categoryTrees, campaign });
    }, [campaignID, history, getSaleCategoriesApi, getCampaignApi]);

    useEffect(() => {
        fetchPageData();
    }, [fetchPageData]);

    // 分類樹，載入前為空陣列
    const categoryTrees = ensureArray(pageData?.categoryTrees);
    // 目前的抵用券類型：使用者選過的優先，其次依白名單推回；未開啟限制時預設第一個選項，已開啟但推不出來時不預選
    const campaignType =
        limit.campaignType || getCampaignTypeValue(options.campaignTypes, limit.includeItems) || (limit.isLimited ? '' : String(ensureArray(options.campaignTypes)[0]?.value ?? ''));

    // 組出以類型預設分類為白名單的名單內容；黑名單已有相同項目時移除並提示，維持兩份名單互斥
    const resolveDefaultLimitItems = type => {
        const includeItems = buildDefaultIncludeItems(categoryTrees, type);
        const includeKeys = includeItems.map(getItemKey);
        const conflicts = limit.excludeItems.filter(item => includeKeys.includes(getItemKey(item)));
        if (conflicts.length > 0) {
            enqueueSnackbar(`「${conflicts.map(({ displayName }) => displayName).join('、')}」已從黑名單移除，改列入白名單`, { variant: 'info' });
        }
        return { includeItems, excludeItems: limit.excludeItems.filter(item => !includeKeys.includes(getItemKey(item))) };
    };

    // 以指定類型的預設分類取代白名單
    const applyCampaignType = nextType => {
        setLimit({ ...limit, campaignType: nextType, ...resolveDefaultLimitItems(nextType) });
    };

    // 切換抵用券類型：白名單為預設狀態直接取代；已有自訂項目時先確認，取消則維持原類型
    const handleChangeCampaignType = nextType => {
        if (!limit.isLimited) {
            // 限制關閉時白名單為預設狀態就先清空，開啟限制時再依新類型帶入
            const includeItems = isDefaultIncludeItems(options.campaignTypes, limit.includeItems) ? [] : limit.includeItems;
            setLimit({ ...limit, campaignType: nextType, includeItems });
            return;
        }
        if (isDefaultIncludeItems(options.campaignTypes, limit.includeItems)) {
            applyCampaignType(nextType);
            return;
        }
        const typeText = ensureArray(options.campaignTypes).find(({ value }) => String(value) === String(nextType))?.text ?? '';
        setMessageDialog({
            open: true,
            title: '切換抵用券類型',
            msg: `白名單已有自訂項目，要改用「${typeText}」的預設分類取代嗎？`,
            buttonText: '確認取代',
            onConfirm: () => applyCampaignType(nextType),
        });
    };

    // 切換商品限制：開啟時若白名單是空的，帶入目前類型的預設分類
    const handleToggleLimit = isLimited => {
        if (isLimited && limit.includeItems.length === 0) {
            setLimit({ ...limit, isLimited, ...resolveDefaultLimitItems(campaignType) });
            return;
        }
        setLimit({ ...limit, isLimited });
    };

    // 加入名單項目：同一項目不可同時在白名單與黑名單
    const handleAddItem = (listKey, item) => {
        const itemKey = getItemKey(item);
        const other = OTHER_LIST[listKey];
        if (limit[other.key].some(target => getItemKey(target) === itemKey)) {
            enqueueSnackbar(`「${item.displayName}」已在${other.label}，同一項目不可同時出現在白名單與黑名單`, { variant: 'warning' });
            return;
        }
        if (limit[listKey].some(target => getItemKey(target) === itemKey)) return;
        setLimit(prev => ({ ...prev, [listKey]: [...prev[listKey], item] }));
    };

    // 移除名單項目
    const handleRemoveItem = (listKey, item) => {
        const itemKey = getItemKey(item);
        setLimit(prev => ({ ...prev, [listKey]: prev[listKey].filter(target => getItemKey(target) !== itemKey) }));
    };

    // 儲存：欄位檢核後新增或修改，成功導回總覽；後端欄位錯誤標到欄位上，其他錯誤由 useAxios 顯示
    const handleSave = async () => {
        if (basicFormRef.current.isError()) return;
        const basic = basicFormRef.current.getResult();
        if (basic.startDate > basic.endDate) {
            enqueueSnackbar('使用開始日不可晚於結束日', { variant: 'warning' });
            return;
        }
        if (limit.isLimited && limit.includeItems.length === 0 && limit.excludeItems.length === 0) {
            enqueueSnackbar(LIMIT_ITEMS_REQUIRED_MESSAGE, { variant: 'warning' });
            return;
        }
        const payload = buildCampaignPayload({ ...basic, isLimited: limit.isLimited, includeItems: limit.includeItems, excludeItems: limit.excludeItems });
        clearValidationErrors();
        const resp = isEdit ? await putCampaignApi(campaignID, { ...payload, campaignID: Number(campaignID) }) : await postCampaignApi(payload);
        if (resp?.isError) {
            updateValidationErrors(resp.result);
            return;
        }
        if (resp) history.push(CAMPAIGN_OVERVIEW_ROUTER);
    };

    return (
        <PageLayout>
            <PageLayout.Header title={isEdit ? '編輯抵用券' : '新增抵用券'} />
            {pageData && !isEmpty(options) && (
                <>
                    <CampaignBasicForm ref={basicFormRef} options={options} validationErrors={validationErrors} campaign={pageData.campaign} isUsed={!!pageData.campaign?.isUsed} />
                    <CampaignProductLimit
                        limitError={validationErrors.includeItems}
                        isLimited={limit.isLimited}
                        campaignType={campaignType}
                        campaignTypes={options.campaignTypes}
                        includeItems={limit.includeItems}
                        excludeItems={limit.excludeItems}
                        categoryTrees={categoryTrees}
                        onToggleLimit={handleToggleLimit}
                        onChangeCampaignType={handleChangeCampaignType}
                        onAdd={handleAddItem}
                        onRemove={handleRemoveItem}
                    />
                    <Stack justifyContent="end" spacing={1} className="campaign-edit-actions">
                        <Button component={Link} to={CAMPAIGN_OVERVIEW_ROUTER} variant="outlined" color="primary">
                            取消
                        </Button>
                        <Button variant="contained" color="primary" onClick={handleSave}>
                            確認送出
                        </Button>
                    </Stack>
                </>
            )}
        </PageLayout>
    );
};

export default CampaignEditPage;
